import { logSwallowed } from '@/lib/errors';
// ============================================================================
// VideoCallPanel.tsx — видеозвонок в чате (WebRTC/LiveKit), полноэкранный
// «мессенджер»-экран.
//   • мгновенное локальное превью (getUserMedia ДО сети, публикуются те же треки);
//   • удалённый — на весь экран, локальный — PiP; при 2+ — сетка;
//   • ЗВУК: startAudio() (политика автоплея) + управление громкостью удалённых
//     голосов (RemoteAudioTrack.setVolume); кнопка «Включить звук», если браузер
//     заблокировал воспроизведение;
//   • КАЧЕСТВО: пресеты (Авто/Низкое/Среднее/Высокое) — разрешение/FPS захвата и
//     maxBitrate публикации; выбор сохраняется (применяется со следующего звонка);
//   • дозвон: гудки, пока никто не ответил; отказ («chat.call.declined») —
//     завершение, причём и если отказ пришёл ВО ВРЕМЯ подключения (гонка состояний);
//   • ошибки явные; при намеренном закрытии/реконнекте — не шумим.
// ============================================================================

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, Settings, Volume2, VolumeX, X, UserPlus, Minimize2, Maximize2, Check, Link2 } from 'lucide-react';
import { Room, RoomEvent, RemoteParticipant, RemoteAudioTrack, Track, Participant } from 'livekit-client';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { logEvent } from '@/lib/eventLog';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { startRingback, stopRingtone } from '@/lib/ringtone';
import { settingsStore } from '@/lib/settingsStore';
import { createLogger } from '@/lib/logger';
import { filterPeople } from '@/lib/people';
import { InviteLinkDialog } from '@/components/chat/InviteLinkDialog';
import { useBreakpoint } from '@/hooks/useBreakpoint';

/** Цель ссылки-приглашения чата (resolve делает вызывающий: чат или обсуждение). */
export interface InviteTarget {
  conversationId: string;
  title?: string;
  note?: string;
}

interface Props {
  roomName: string; // комната = conv_{conversation_id}
  identity: string; // id текущего пользователя
  myName?: string; // имя звонящего (для приглашений)
  /** Звонок в состоянии сервера (CallService): при завершении панель закрывает
   *  и серверный звонок (call.ended) — иначе звонок остался бы «активным»
   *  (и мог бы ещё раз всплыть после перезагрузки). Нет id — только шина. */
  callId?: string;
  onLeave: () => void;
  /** Резолвер ссылки-приглашения. При отсутствии ссылки для комнаты недоступны
   *  (ad-hoc комната без привязанного чата — используйте «Пригласить»). */
  getInviteTarget?: () => Promise<InviteTarget>;
}

type CallQuality = 'auto' | 'low' | 'medium' | 'high';

const QUALITY_PRESETS: Record<CallQuality, { w: number; h: number; fps: number; bitrate: number }> = {
  low: { w: 640, h: 360, fps: 24, bitrate: 400_000 },
  medium: { w: 1280, h: 720, fps: 30, bitrate: 1_200_000 },
  high: { w: 1920, h: 1080, fps: 30, bitrate: 2_500_000 },
  auto: { w: 1280, h: 720, fps: 30, bitrate: 1_500_000 },
};

// ============================================================================
// PiP (свёрнутый звонок): позиция — в пользовательских настройках (settingsStore,
// SSoT — user_settings), восстанавливается при следующем звонке. По умолчанию —
// справа сверху, ПОД шапкой чата; на мобильном нижняя граница не заходит под
// док/нижнюю панель (52px + отступ).
// ============================================================================
const PIP_SETTINGS_KEY = 'call.pip';
const PIP_MARGIN = 8;
const PIP_TOP_OFFSET = 64;
const PIP_MOBILE_BOTTOM = 64;

interface PipPos { x: number; y: number }

function readPipPos(): PipPos | null {
  const v = settingsStore.getJSON<PipPos | null>(PIP_SETTINGS_KEY, null);
  if (!v || !Number.isFinite(v.x) || !Number.isFinite(v.y)) return null;
  return { x: Number(v.x), y: Number(v.y) };
}

function savePipPos(pos: PipPos): void {
  settingsStore.setJSON(PIP_SETTINGS_KEY, pos);
}

const log = createLogger('VideoCallPanel');

// ============================================================================
// Настройки звонка — в ХРАНИЛИЩЕ НАСТРОЕК (settingsStore → user_settings):
// серверный SSoT, переживают смену устройства/браузера/базы; localStorage —
// только мгновенный кэш/офлайн.
// ВАЖНО: Number(null) === 0 — из-за этого пустая настройка ранее давала
// громкость 0 («по умолчанию тишина, правлю каждый раз»). Нет настройки → 1.
// ============================================================================
function readCallQuality(): CallQuality {
  const stored = settingsStore.get('call.quality');
  const legacy = localStorage.getItem('hydra_call_quality') || '';
  const v = stored !== undefined && stored !== '' ? stored : legacy;
  return v === 'low' || v === 'medium' || v === 'high' ? v : 'auto';
}

function readCallVolume(): number {
  const stored = settingsStore.get('call.volume');
  const legacy = localStorage.getItem('hydra_call_volume');
  const src = stored !== undefined && stored !== '' ? stored : (legacy == null ? '' : legacy);
  if (src === '') return 1;
  const v = Number(src);
  return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
}

function saveCallPref(key: 'call.quality' | 'call.volume', value: string, legacyKey: string): void {
  settingsStore.set(key, value);
  localStorage.setItem(legacyKey, value);
}

const isEngineClosedError = (msg: string): boolean =>
  /pc manager|closed|not connected|disconnected|negotiation/i.test(msg);

export function VideoCallPanel({ roomName, identity, myName, callId, onLeave, getInviteTarget }: Props) {
  const [status, setStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  // КАМЕРА ПО УМОЛЧАНИЮ ВЫКЛЮЧЕНА (звонок начинается с микрофона; камера —
  // явным нажатием кнопки). Запрос getUserMedia не запрашивает видео, пока
  // пользователь не включит камеру.
  const [cameraOff, setCameraOff] = useState(true);
  const [participants, setParticipants] = useState<string[]>([]);
  const [speaking, setSpeaking] = useState<Set<string>>(new Set());
  const [quality, setQuality] = useState<CallQuality>(() => readCallQuality());
  const [volume, setVolume] = useState<number>(() => readCallVolume());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [needAudioTap, setNeedAudioTap] = useState(false);
  const [echoWarn, setEchoWarn] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteList, setInviteList] = useState<Array<{ id: string; name: string }>>([]);
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteSel, setInviteSel] = useState<Set<string>>(new Set());
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<InviteTarget | null>(null);
  const [inviteLinkBusy, setInviteLinkBusy] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const declinedRef = useRef(false);
  const earlyStreamRef = useRef<MediaStream | null>(null);
  const showToast = useToastStore((s) => s.showError);
  const conversationId = roomName.startsWith('conv_') ? roomName.slice(5) : roomName;
  const { isMobile } = useBreakpoint();
  // PiP: позиция (px) в настройках; перетаскивание — pointer events, границы окна.
  const [pipPos, setPipPos] = useState<PipPos | null>(readPipPos);
  const pipPosRef = useRef<PipPos | null>(pipPos);
  pipPosRef.current = pipPos;
  const pipRef = useRef<HTMLDivElement | null>(null);
  const pipDragRef = useRef<{ dx: number; dy: number; moved: boolean; pointerId: number } | null>(null);

  const remotes = participants.filter((p) => p !== identity);
  const iSpeak = speaking.has(identity);

  // Границы PiP: внутри окна, сверху — под шапкой чата, снизу — над доком
  // (на мобильном панель задач 52px + отступ; PiP не уходит под неё).
  const clampPip = (x: number, y: number): PipPos => {
    const rect = pipRef.current?.getBoundingClientRect();
    const w = rect?.width || 208;
    const h = rect?.height || 176;
    const bottomReserve = (isMobile ? PIP_MOBILE_BOTTOM : PIP_MARGIN) + PIP_MARGIN;
    const maxX = Math.max(PIP_MARGIN, window.innerWidth - w - PIP_MARGIN);
    const maxY = Math.max(PIP_TOP_OFFSET, window.innerHeight - h - bottomReserve);
    return {
      x: Math.min(Math.max(PIP_MARGIN, x), maxX),
      y: Math.min(Math.max(PIP_TOP_OFFSET, y), maxY),
    };
  };

  // Позиция при входе в свёрнутый режим / ресайзе окна: сохранённая (из
  // настроек) либо справа сверху под шапкой; всегда в пределах окна.
  useEffect(() => {
    if (!minimized) return;
    const place = () => {
      setPipPos((prev) => {
        if (prev) {
          const c = clampPip(prev.x, prev.y);
          return c.x === prev.x && c.y === prev.y ? prev : c;
        }
        const rect = pipRef.current?.getBoundingClientRect();
        const w = rect?.width || 208;
        return clampPip(window.innerWidth - w - PIP_MARGIN, PIP_TOP_OFFSET);
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('orientationchange', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized, isMobile]);

  const onPipPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = pipRef.current?.getBoundingClientRect();
    if (!rect) return;
    pipDragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, moved: false, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPipPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = pipDragRef.current;
    if (!d) return;
    const next = clampPip(e.clientX - d.dx, e.clientY - d.dy);
    if (Math.abs(next.x - (pipPosRef.current?.x ?? next.x)) > 1 || Math.abs(next.y - (pipPosRef.current?.y ?? next.y)) > 1) {
      d.moved = true;
    }
    // Ref — синхронно (pointerup может прийти раньше перерисовки состояния).
    pipPosRef.current = next;
    setPipPos(next);
  };

  const onPipPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = pipDragRef.current;
    if (!d) return;
    pipDragRef.current = null;
    if (e.currentTarget.hasPointerCapture(d.pointerId)) e.currentTarget.releasePointerCapture(d.pointerId);
    // Записываем настройку ОДИН раз — по завершении жеста (не на каждый пиксель).
    if (d.moved && pipPosRef.current) savePipPos(pipPosRef.current);
  };

  // Гудки дозвона: пока никто не подключился; при отказе/завершении — стоп.
  useEffect(() => {
    if (status === 'connected' && remotes.length === 0 && !declinedRef.current) startRingback();
    else stopRingtone();
    return () => stopRingtone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, remotes.length]);

  // Громкость удалённых голосов (через setVolume у RemoteAudioTrack).
  useEffect(() => {
    if (status !== 'connected') return;
    const room = roomRef.current;
    if (!room) return;
    const apply = () => {
      room.remoteParticipants.forEach((p) => {
        p.audioTrackPublications.forEach((pub) => {
          (pub.audioTrack as RemoteAudioTrack | undefined)?.setVolume(volume);
        });
      });
    };
    apply();
    const t = setTimeout(apply, 500);
    room.on(RoomEvent.TrackSubscribed, apply);
    return () => {
      clearTimeout(t);
      room.off(RoomEvent.TrackSubscribed, apply);
    };
  }, [volume, status]);

  // Страховка дозвона: нет ответа 45 c → «Нет ответа» и завершение (гудки не бесконечны).
  useEffect(() => {
    if (status !== 'connected' || remotes.length > 0 || declinedRef.current) return;
    const t = window.setTimeout(() => {
      stopRingtone();
      void logEvent({
        level: 'warning',
        category: 'ui',
        message: 'видеозвонок: нет ответа (45 c)',
        entityType: 'chat',
        entityCode: 'call',
        objectId: roomName,
      });
      showToast('Нет ответа');
      onLeave();
    }, 45000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, remotes.length]);

  // Настройки звонка гидратируются АСИНХРОННО из user_settings — подхватываем
  // по подписке (не перетирая текущее значение другими настройками).
  useEffect(() => settingsStore.subscribe(() => {
    setVolume((prev) => {
      const v = readCallVolume();
      return Math.abs(prev - v) > 0.001 ? v : prev;
    });
    setQuality((prev) => {
      const q = readCallQuality();
      return prev !== q ? q : prev;
    });
  }), []);

  // Основной жизненный цикл звонка.
  useEffect(() => {
    let cancelled = false;
    declinedRef.current = false;
    let earlyStream: MediaStream | null = null;

    const unsub = eventBusHub.subscribe((ev) => {
      if (ev.topic === `chat.call.declined.${conversationId}` && ev.payload?.user_id !== identity) {
        if (cancelled) return;
        void logEvent({
          level: 'info',
          category: 'ui',
          message: 'видеозвонок: получен отказ второй стороны',
          entityType: 'chat',
          entityCode: 'call',
          objectId: roomName,
          details: JSON.stringify({ v: String(ev.payload?.v || '') }),
        });
        declinedRef.current = true;
        stopRingtone();
        showToast('Звонок отклонён');
        onLeave();
      }
    });

    (async () => {
      // МГНОВЕННОЕ превью: камера/микрофон — СРАЗУ (до сети); после подключения
      // ЭТИ ЖЕ треки публикуются в LiveKit (без повторного захвата).
      const preset = QUALITY_PRESETS[readCallQuality()];
      const mediaPromise = (async () => {
        try {
          // Камера по умолчанию ВЫКЛЮЧЕНА: захватываем только микрофон.
          // При включении камеры LiveKit сам запросит видео (setCameraEnabled).
          const s = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
          earlyStream = s;
          earlyStreamRef.current = s;
          const el = localVideoRef.current;
          if (el) el.srcObject = s;
          return s;
        } catch {
          return null;
        }
      })();

      try {
        const r: any = await getSDK().media.createLiveKitToken({ room: roomName } as any);
        const url = String(r?.url || '');
        const token = String(r?.token || '');
        if (!url || !token) throw new Error('Видеозвонки недоступны (LiveKit не настроен)');
        if (declinedRef.current || cancelled) return;

        const room = new Room();
        roomRef.current = room;

        room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
          setParticipants((prev) => (prev.includes(p.identity) ? prev : [...prev, p.identity]));
        });
        room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
          setParticipants((prev) => prev.filter((x) => x !== p.identity));
        });
        room.on(RoomEvent.TrackSubscribed, (_t: unknown, _pub: unknown, p: RemoteParticipant) => {
          setParticipants((prev) => (prev.includes(p.identity) ? prev : [...prev, p.identity]));
        });
        room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
          setSpeaking(new Set(speakers.map((s) => s.identity)));
        });
        room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
          setNeedAudioTap(!room.canPlaybackAudio);
        });
        room.on(RoomEvent.SignalReconnecting, () => {
          if (!cancelled) {
            void logEvent({
              level: 'warning',
              category: 'ui',
              message: 'видеозвонок: сигнальный канал переподключается',
              entityType: 'chat',
              entityCode: 'call',
              objectId: roomName,
              details: JSON.stringify({ url }),
            });
          }
        });
        room.on(RoomEvent.Disconnected, (reason?: unknown) => {
          if (!cancelled) {
            void logEvent({
              level: 'warning',
              category: 'ui',
              message: 'видеозвонок: соединение закрыто',
              entityType: 'chat',
              entityCode: 'call',
              objectId: roomName,
              details: JSON.stringify({ reason: String(reason ?? ''), url }),
            });
            onLeave();
          }
        });

        await room.connect(url, token);
        if (cancelled || declinedRef.current) { await room.disconnect(); return; }
        (window as any).__hydraVideoRoom = room;

        // Звук: снимаем блокировку автоплея (вызов из жеста пользователя).
        try {
          await room.startAudio();
          setNeedAudioTap(!room.canPlaybackAudio);
        } catch {
          setNeedAudioTap(true);
        }

        setStatus('connected');

        const captured = await mediaPromise;
        if (captured) earlyStream = captured;
        // Диагностика звука: фактические настройки микрофона (эхо-подавление и пр.)
        // и число параллельно играющих аудио-элементов (для отлова дублей).
        try {
          const s = captured?.getAudioTracks?.()[0]?.getSettings?.();
          if (s) {
            void logEvent({
              level: 'info',
              category: 'ui',
              message: 'видеозвонок: настройки микрофона',
              entityType: 'chat',
              entityCode: 'call',
              objectId: roomName,
              details: JSON.stringify({
                echoCancellation: s.echoCancellation,
                noiseSuppression: s.noiseSuppression,
                autoGainControl: s.autoGainControl,
              }),
            });
            if (s.echoCancellation === false) setEchoWarn(true);
          }
        } catch { /* диагностика не критична */ }
        window.setTimeout(() => {
          if (!cancelled) {
            void logEvent({
              level: 'info',
              category: 'ui',
              message: 'видеозвонок: аудио-диагностика',
              entityType: 'chat',
              entityCode: 'call',
              objectId: roomName,
              details: JSON.stringify({ audioElements: document.querySelectorAll('audio[data-call-audio]').length }),
            });
          }
        }, 5000);
        const local = room.localParticipant;
        setParticipants((prev) => [...new Set([local.identity, ...prev])]);
        try {
          if (captured) {
            const v = captured.getVideoTracks()[0];
            const a = captured.getAudioTracks()[0];
            if (v) {
              await local.publishTrack(v, {
                source: Track.Source.Camera,
                videoEncoding: { maxBitrate: preset.bitrate, maxFramerate: preset.fps },
              });
            }
            if (a) await local.publishTrack(a, { source: Track.Source.Microphone });
          } else {
            // Нет захваченного потока — включаем ТОЛЬКО микрофон (камера по
            // умолчанию выключена и включается пользователем).
            await local.setMicrophoneEnabled(true);
          }
        } catch (mediaErr: unknown) {
          if (cancelled || roomRef.current !== room || room.state === 'reconnecting' || declinedRef.current) return;
          const msg = String((mediaErr as Error)?.message || '');
          if (isEngineClosedError(msg) || room.state !== 'connected') {
            setStatus('error');
            setError('Соединение с сервером звонка прервано');
          } else {
            setError('Камера/микрофон недоступны: ' + (msg || 'нет доступа'));
          }
        }
      } catch (e: unknown) {
        if (!cancelled && !declinedRef.current) {
          const msg = String((e as Error)?.message || 'Не удалось подключиться к видеозвонку');
          setStatus('error');
          setError(msg);
          showToast(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsub();
      stopRingtone();
      if (earlyStream) earlyStream.getTracks().forEach((t: MediaStreamTrack) => { if (t.readyState === 'live') t.stop(); });
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.disconnect();
      delete (window as any).__hydraVideoRoom;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  // Привязка ЛОКАЛЬНОГО видео (после монтирования <video> и при публикации трека).
  useEffect(() => {
    if (status !== 'connected') return;
    const room = roomRef.current;
    if (!room) return;
    const attachLocal = () => {
      const el = localVideoRef.current;
      if (!el) return;
      if (!el.srcObject && earlyStreamRef.current) el.srcObject = earlyStreamRef.current;
      room.localParticipant.videoTrackPublications.forEach((pub) => {
        if (!pub.videoTrack || pub.source !== Track.Source.Camera) return;
        const cur = el.srcObject instanceof MediaStream ? el.srcObject.getVideoTracks()[0] : null;
        if (cur && pub.videoTrack.mediaStreamTrack && cur === pub.videoTrack.mediaStreamTrack) return;
        pub.videoTrack.attach(el);
      });
    };
    attachLocal();
    const timers = [150, 500, 1200].map((ms) => window.setTimeout(attachLocal, ms));
    room.on(RoomEvent.LocalTrackPublished, attachLocal);
    room.on(RoomEvent.TrackUnmuted, attachLocal);
    return () => {
      timers.forEach((t) => clearTimeout(t));
      room.off(RoomEvent.LocalTrackPublished, attachLocal);
      room.off(RoomEvent.TrackUnmuted, attachLocal);
    };
  }, [status, remotes.length, minimized]);

  const leave = () => {
    stopRingtone();
    // Серверная запись звонка (механизм ядра): завершение — состояние + событие
    // call.ended.{id} (другие устройства/участники закроют экран сразу).
    if (callId) {
      void getSDK().calls.endCall({ callId })
        .catch((e: unknown) => log.warn('call.endCall failed', { error: String((e as Error)?.message || e) }));
    }
    getSDK().eventBus.publish({
      topic: `chat.call.ended.${conversationId}`,
      payload: JSON.stringify({ user_id: identity }),
    }).catch((e) => { logSwallowed(e, 'components/chat/VideoCallPanel.tsx:424'); return undefined; });
    onLeave();
  };

  const toggleMic = async () => {
    const room = roomRef.current;
    const p = room?.localParticipant;
    if (!room || !p || (room.state !== 'connected' && room.state !== 'reconnecting')) { showToast('Подключение…'); return; }
    const next = !muted;
    setMuted(next);
    try {
      await p.setMicrophoneEnabled(!next);
    } catch (e: unknown) {
      setMuted(!next);
      const msg = String((e as Error)?.message || '');
      showToast(isEngineClosedError(msg) ? 'Связь с сервером звонка потеряна' : 'Микрофон: ' + (msg || 'ошибка'));
    }
  };

  const toggleCam = async () => {
    const room = roomRef.current;
    const p = room?.localParticipant;
    if (!room || !p || (room.state !== 'connected' && room.state !== 'reconnecting')) { showToast('Подключение…'); return; }
    const next = !cameraOff;
    setCameraOff(next);
    try {
      await p.setCameraEnabled(!next);
    } catch (e: unknown) {
      setCameraOff(!next);
      const msg = String((e as Error)?.message || '');
      showToast(isEngineClosedError(msg) ? 'Связь с сервером звонка потеряна' : 'Камера: ' + (msg || 'ошибка'));
    }
  };

  const enableAudio = () => {
    const room = roomRef.current;
    if (!room) return;
    void room.startAudio().then(() => setNeedAudioTap(!room.canPlaybackAudio)).catch((e) => { logSwallowed(e, 'components/chat/VideoCallPanel.tsx:461'); return undefined; });
    document.querySelectorAll('audio[data-call-audio]').forEach((el) => {
      void (el as HTMLAudioElement).play().catch((e) => { logSwallowed(e, 'components/chat/VideoCallPanel.tsx:463'); return undefined; });
    });
  };

  const openInvite = () => {
    setInviteOpen(true);
    setInviteQuery('');
    setInviteSel(new Set());
    // peopleOnly — приглашаем ЛЮДЕЙ (системные учётки/боты скрыты сервером).
    getSDK().entity.listEntities({ peopleOnly: true }).then((r: any) => {
      const items = filterPeople(((r?.items || []) as any[]))
        .map((u) => ({ id: String(u.id), name: String(u.name || u.login || u.id) }))
        .filter((u) => u.id !== identity && u.id !== '');
      setInviteList(items);
    }).catch((e: unknown) => {
      void logEvent({
        level: 'error',
        category: 'ui',
        message: 'звонок: не удалось получить список пользователей',
        entityType: 'chat',
        entityCode: 'call',
        objectId: roomName,
        details: String((e as Error)?.message || e),
      });
    });
  };

  const sendInvites = () => {
    const ids = Array.from(inviteSel);
    if (ids.length === 0) { setInviteOpen(false); return; }
    getSDK().eventBus.publish({
      topic: `chat.call.started.${conversationId}`,
      payload: JSON.stringify({ user_id: identity, name: myName || '', invite: ids }),
    }).then(() => showToast(ids.length === 1 ? 'Приглашение отправлено' : `Приглашения отправлены (${ids.length})`))
      .catch((e: unknown) => showToast('Не удалось пригласить: ' + String((e as Error)?.message || e)));
    setInviteOpen(false);
  };

  // «Пригласить по ссылке»: цель резолвит вызывающий (чат — сама комната;
  // ad-hoc — обсуждение объекта). Ссылки для комнат без чата недоступны.
  const openInviteLink = async () => {
    if (!getInviteTarget || inviteLinkBusy) return;
    setInviteLinkBusy(true);
    try {
      const t = await getInviteTarget();
      if (!t?.conversationId) throw new Error('Не удалось определить чат для ссылки-приглашения');
      setInviteTarget(t);
      setInviteLinkOpen(true);
    } catch (e: unknown) {
      showToast('Приглашение по ссылке: ' + friendlyError(e));
      void logEvent({
        level: 'error',
        category: 'ui',
        message: 'звонок: не удалось подготовить ссылку-приглашение',
        entityType: 'chat',
        entityCode: 'call',
        objectId: roomName,
        details: String((e as Error)?.message || e),
      });
    } finally {
      setInviteLinkBusy(false);
    }
  };

  const statusText = status === 'connecting'
    ? 'Подключение…'
    : status === 'error'
      ? (error || 'Звонок завершён')
      : remotes.length === 0
        ? 'Ожидание ответа…'
        : remotes.length === 1
          ? 'Собеседник в звонке'
          : `${remotes.length} участника(ов)`;

  // Свёрнутый режим: продолжаем работу, звонок — маленьким окном (PiP).
  // По умолчанию справа сверху ПОД шапкой чата (не закрывает поле ввода);
  // перетаскивается мышью/пальцем, границы — окно и док; позиция запоминается.
  if (minimized) {
    return (
      <div
        ref={pipRef}
        data-call-pip
        className="fixed z-[12500] w-52 overflow-hidden rounded-xl border border-white/20 bg-black/90 text-white shadow-2xl select-none"
        style={pipPos ? { left: pipPos.x, top: pipPos.y } : { right: 16, top: PIP_TOP_OFFSET }}
        onPointerDown={onPipPointerDown}
        onPointerMove={onPipPointerMove}
        onPointerUp={onPipPointerUp}
        onPointerCancel={onPipPointerUp}
      >
        {remotes.map((p) => {
          return <RemoteAudio key={`a-${p}`} identity={p} volume={volume} />;
        })}
        <div className="relative h-32 bg-black cursor-move" style={{ touchAction: 'none' }} title="Перетащите, чтобы переместить">
          {remotes.length > 0 ? (
            <RemoteVideo identity={remotes[0]} speaking={speaking.has(remotes[0])} />
          ) : (
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" />
          )}
          {cameraOff && remotes.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/70">
              <VideoOff className="h-6 w-6 text-white/50" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
          <span className="min-w-0 flex-1 truncate text-[10px] text-white/70">{statusText}</span>
          <button type="button" title="Развернуть" onClick={() => setMinimized(false)} className="rounded-full p-1 hover:bg-white/15">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button type="button" title="Положить трубку" onClick={leave} className="rounded-full bg-red-600 p-1 hover:bg-red-500">
            <PhoneOff className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[12500] flex flex-col bg-black text-white">
      {remotes.map((p) => {
        return <RemoteAudio key={`a-${p}`} identity={p} volume={volume} />;
      })}
      {/* Шапка */}
      <div className="relative flex items-center justify-between px-4 py-3 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">Видеозвонок</div>
          <div className={`text-[11px] ${status === 'error' ? 'text-red-400' : 'text-white/60'}`}>{statusText}</div>
        </div>
        <div className="flex items-center gap-2">
          {status === 'connecting' && <Loader2 className="h-4 w-4 animate-spin text-white/60" />}
          <button
            type="button"
            title="Пригласить собеседника"
            onClick={openInvite}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
          >
            <UserPlus className="h-4 w-4" />
          </button>
          {getInviteTarget && (
            <button
              type="button"
              title="Пригласить по ссылке"
              onClick={() => void openInviteLink()}
              disabled={inviteLinkBusy}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-50"
            >
              {inviteLinkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            </button>
          )}
          <button
            type="button"
            title="Свернуть (продолжить работу)"
            onClick={() => setMinimized(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Настройки звонка"
            onClick={() => setSettingsOpen((v) => !v)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Видео */}
      <div className="relative flex-1 min-h-0">
        {remotes.length === 0 && (
          <div className="relative h-full w-full">
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" />
            {cameraOff && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <VideoOff className="h-12 w-12 text-white/40" />
              </div>
            )}
            <span className="absolute bottom-3 left-4 flex items-center gap-1.5 text-[12px] bg-black/50 rounded-full px-3 py-1">
              Вы
              {iSpeak && <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />}
            </span>
          </div>
        )}

        {remotes.length === 1 && (
          <>
            <RemoteVideo identity={remotes[0]} speaking={speaking.has(remotes[0])} />
            <div className={`absolute top-3 right-3 h-36 w-24 sm:h-44 sm:w-32 overflow-hidden rounded-xl border bg-black shadow-lg ${iSpeak ? 'border-green-500 ring-2 ring-green-500' : 'border-white/20'}`}>
              <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" />
            </div>
          </>
        )}

        {remotes.length > 1 && (
          <div className="grid h-full grid-cols-2 gap-2 overflow-y-auto p-2">
            {remotes.map((p) => (
              <div key={p} className="relative aspect-video overflow-hidden rounded-lg bg-black/50">
                <RemoteVideo identity={p} speaking={speaking.has(p)} />
              </div>
            ))}
            <div className={`relative aspect-video overflow-hidden rounded-lg border bg-black ${iSpeak ? 'border-green-500 ring-2 ring-green-500' : 'border-white/20'}`}>
              <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" />
            </div>
          </div>
        )}

        {needAudioTap && status === 'connected' && (
          <button
            type="button"
            onClick={enableAudio}
            className="absolute left-1/2 top-3 -translate-x-1/2 flex items-center gap-2 rounded-full bg-white text-black px-4 py-1.5 text-xs font-medium shadow-lg"
          >
            <Volume2 className="h-4 w-4" /> Включить звук
          </button>
        )}

        {echoWarn && status === 'connected' && (
          <div className="absolute left-1/2 top-12 -translate-x-1/2 rounded-full bg-yellow-500/95 px-4 py-1 text-[11px] font-medium text-black shadow-lg">
            Микрофон без эхо-подавления — наденьте наушники (иначе будет эхо)
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 px-6 text-center">
            <PhoneOff className="h-10 w-10 text-red-500" />
            <div className="text-sm text-white/90">{error}</div>
            <button
              type="button"
              className="mt-1 rounded-full bg-white/10 px-4 py-1.5 text-xs hover:bg-white/20"
              onClick={leave}
            >
              Закрыть
            </button>
          </div>
        )}
      </div>

      {/* Контролы */}
      <div className="flex items-center justify-center gap-5 py-4 shrink-0">
        <button
          type="button"
          onClick={() => void toggleMic()}
          aria-label={muted ? 'Включить микрофон' : 'Выключить микрофон'}
          className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${muted ? 'bg-white text-black' : 'bg-white/15 hover:bg-white/25'}`}
        >
          {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        <button
          type="button"
          onClick={leave}
          aria-label="Положить трубку"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 hover:bg-red-500"
        >
          <PhoneOff className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={() => void toggleCam()}
          aria-label={cameraOff ? 'Включить камеру' : 'Выключить камеру'}
          className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${cameraOff ? 'bg-white text-black' : 'bg-white/15 hover:bg-white/25'}`}
        >
          {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </button>
      </div>

      {/* Настройки звонка — нижний лист (мобильный вид) */}
      {settingsOpen && (
        <>
          <button type="button" aria-label="Закрыть настройки" className="absolute inset-0 z-[15] cursor-default" onClick={() => setSettingsOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 z-[16] mx-auto w-full max-w-md rounded-t-2xl border-t border-white/15 bg-black/95 px-4 pb-6 pt-4 text-white shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm font-medium">Настройки звонка</span>
              <button type="button" onClick={() => setSettingsOpen(false)} className="rounded-full p-1 hover:bg-white/10">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mb-4">
              <div className="mb-2 text-[11px] text-white/60">Качество видео (применится со следующего звонка)</div>
              <div className="grid grid-cols-4 gap-2">
                {([['auto', 'Авто'], ['low', 'Низкое'], ['medium', 'Среднее'], ['high', 'Высокое']] as Array<[CallQuality, string]>).map(([q, label]) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => { setQuality(q); saveCallPref('call.quality', q, 'hydra_call_quality'); }}
                    className={`rounded-full px-2 py-1.5 text-[11px] ${quality === q ? 'bg-white font-medium text-black' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[10px] text-white/40">
                {quality === 'high' ? '1080p · ~2.5 Мбит/с' : quality === 'medium' ? '720p · ~1.2 Мбит/с' : quality === 'low' ? '360p · ~0.4 Мбит/с' : '720p · ~1.5 Мбит/с'}
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] text-white/60">
                <span>Громкость собеседников</span>
                <span>{Math.round(volume * 100)}%</span>
              </div>
              <div className="flex items-center gap-3">
                <VolumeX className="h-4 w-4 shrink-0 text-white/50" />
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={volume}
                  onChange={(e) => { const v = Number(e.target.value); setVolume(v); saveCallPref('call.volume', String(v), 'hydra_call_volume'); }}
                  className="h-1.5 flex-1 cursor-pointer accent-white"
                />
                <Volume2 className="h-4 w-4 shrink-0 text-white/50" />
              </div>
              <div className="mt-1 text-[10px] text-white/40">Совет: наушники устраняют эхо и гул.</div>
            </div>
          </div>
        </>
      )}

      {/* Приглашение собеседника */}
      {inviteOpen && (
        <>
          <button type="button" aria-label="Закрыть" className="absolute inset-0 z-[15] cursor-default" onClick={() => setInviteOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 z-[16] mx-auto flex max-h-[70vh] w-full max-w-md flex-col rounded-t-2xl border-t border-white/15 bg-black/95 px-4 pb-6 pt-4 text-white shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium">Пригласить собеседника</span>
              <button type="button" onClick={() => setInviteOpen(false)} className="rounded-full p-1 hover:bg-white/10">
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              value={inviteQuery}
              onChange={(e) => setInviteQuery(e.target.value)}
              placeholder="Поиск по имени…"
              className="mb-2 w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none placeholder:text-white/40"
            />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {inviteList
                .filter((u) => !inviteQuery.trim() || u.name.toLowerCase().includes(inviteQuery.trim().toLowerCase()))
                .map((u) => {
                  const on = inviteSel.has(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setInviteSel((prev) => {
                        const n = new Set(prev);
                        if (n.has(u.id)) n.delete(u.id);
                        else n.add(u.id);
                        return n;
                      })}
                      className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm ${on ? 'bg-white/15' : 'hover:bg-white/5'}`}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? 'border-white bg-white text-black' : 'border-white/40'}`}>
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate">{u.name}</span>
                    </button>
                  );
                })}
              {inviteList.length === 0 && <div className="py-6 text-center text-xs text-white/40">Загрузка…</div>}
            </div>
            <button
              type="button"
              onClick={sendInvites}
              disabled={inviteSel.size === 0}
              className="mt-3 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
            >
              Пригласить{inviteSel.size > 0 ? ` (${inviteSel.size})` : ''}
            </button>
          </div>
        </>
      )}

      {/* Ссылка-приглашение (общий диалог с чатом) */}
      {inviteLinkOpen && inviteTarget && (
        <InviteLinkDialog
          conversationId={inviteTarget.conversationId}
          title={inviteTarget.title}
          note={inviteTarget.note}
          onClose={() => { setInviteLinkOpen(false); setInviteTarget(null); }}
        />
      )}
    </div>
  );
}

// Удалённый участник: полный экран (cover) + подпись; привязка видео по событиям
// и повторными попытками (поздние треки не теряются).
import { RemoteVideo, RemoteAudio } from './videoCallTiles';
